import { Component, Inject } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogActions } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { InformableSectionItem, InformableSection, InformableType } from 'globals/store/informables.data';
import { createId, jsonCopyObject } from 'globals/helpers/helpers';
import { InformablesService } from 'src/app/services/informables.service';
import { ConfirmDeleteData, openConfirmDeleteGetResult } from 'src/app/components/dialog.confirmDelete.component';
import { AssetsComponent } from 'src/app/modules/assets/assets.component';
import { FileHandlerComponent } from '../file-handler/file-handler.component';
import { MatFormFieldModule } from '@angular/material/form-field';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { IconComponent } from '../icon/icon.component';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';


@Component({
    selector: 'informable-dialog',
    template: `
<div class="joi-dialog ">
  <div mat-dialog-content>
    <form>
      <mat-form-field class="general">
        <input matInput name="title" [(ngModel)]="item.name" [placeholder]="data.itemPlaceholder || 'Item name'">
      </mat-form-field>
      <mat-form-field class="general">
        <textarea matInput cdkTextareaAutosize id="description" name="description" [(ngModel)]="item.description" [placeholder] ="data.descriptionPlaceholder || item.placeholder"></textarea>
      </mat-form-field>
      <div class="logistics_expand">
        <h5>Files</h5>
        <file-handler [data]="item" (dataChange)="saveFiles($event)"></file-handler>
        <ng-container *ngIf="!!item.assetIds">
          <h5>Assets</h5>
          <assets 
            [title]="''"
            [titleIcon]="''"
            [assetIds]="item.assetIds || []" 
            [entityHolderName]="data.assetEntityHolderName || ''"
            [type]="'informable'"
            [readOnly]="false"
            (idsAdded)="saveAssetIds($event)"
            (idRemoved)="removeAssetId($event)"
          ></assets>
        </ng-container>
        
      </div>
    </form>
  </div>
  <div mat-dialog-actions>
    <button mat-button class="delete" *ngIf="!addItem && !data.fromAsset" (click)="deleteItem($event)">
      <icon icon="delete_24px" style="position: relative; top: 8px;"></icon>
    </button>
    <div style="flex:1"></div>
    <button mat-button (click)="dialogRef.close()">CANCEL</button>
    <button mat-button *ngIf="!addItem && !data.fromAsset"  (click)="duplicateItem()">DUPLICATE</button>
    <button matButton="filled" color="primary" (click)="saveItem()">SAVE</button>
  </div>
</div>
  `,
    styleUrls: ['./informable.scss'],
    standalone: true,
    imports: [AssetsComponent, MatInputModule, MatButtonModule, MatDialogModule, CommonModule, IconComponent, FileHandlerComponent, MatFormFieldModule, FormsModule]
})
export class InformableDialogComponent {
  item: InformableSectionItem = { id: '', name: '', description: '', placeholder: 'Description', files: [] } // default
  addItem = true
  information: InformableSection[];
  informableType: InformableType;


  constructor(
    public dialogRef: MatDialogRef<InformableDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: InformableEditInfo,
    public store: Store,
    private informableService: InformablesService,
    public dialog: MatDialog,
  ) {
    // console.log('data ', data);
    if (data.item) {
      this.item = jsonCopyObject(data.item)
      this.addItem = false
    } else {
      this.item.id = createId()
    }
    if (data.useAssetIds && !this.item.assetIds) this.item.assetIds = []
    this.information = this.data.information
  }
  async deleteItem(event) {
    const data: ConfirmDeleteData = { title: 'Delete this item', rect: event.target.getBoundingClientRect(), dialog: this.dialog }
    const del = await openConfirmDeleteGetResult(data)
    if (!del) return

    const {section, info, informableId, informableType} = this.getSectionAndInfo(true)
    if (!section) {
      return this.dialogRef.close() // somebody else deleted section
    }
    const itemIndex = this.getItemIndex(section)
    if (itemIndex === -1) {
      return this.dialogRef.close() // somebody else deleted item
    }
    section.items.splice(itemIndex, 1)
    setTimeout(() => {
      this.updateInformable(informableId, info, informableType)
    }, 0) // close before saving, so if there are any changes to save, the user won't see them happen in real time on the screen while the dialog is still open

  }

  getSectionAndInfo(deleting = false): { section: InformableSection, info: InformableSection[], informableId:string, informableType: InformableType, informableKey?:string} { 
    let sectionId = this.information.findIndex(s => s.id === this.data.sectionId)
    if(sectionId === -1 && !deleting){
      sectionId = this.information.length
      this.information.push({
        id: '',
        title: '',
        items: []
      })
    }
    return {section: this.information[sectionId], info: this.information, informableId: this.data.informableId, informableType:this.data.informableType, informableKey:this.data.informableKey}
  }

  getItemIndex(section: InformableSection, isNew = false) {
    const itemIndex = !section.items || !this.data.item ? -1 : section.items.findIndex(item => item.id === this.data.item.id)
    if (itemIndex === -1 && isNew) {
      console.log("Couldn't find the item where it should be.  Either this is an error, or another user deleted the item while you were editing it.", { section, item: this.item })
    }
    return itemIndex
  }

  duplicateItem() {
    const {section, info, informableId, informableType} = this.getSectionAndInfo(true)
    const itemIndex = this.getItemIndex(section)
    this.data.item.id = createId() // after finding the old index above, change the id to a new, unique one
    this.data.item.name = this.informableService.copyItemName(this.data.item.name, section)
    if (itemIndex > -1) {
      section.items.splice(itemIndex, 0, this.item)
    } else {
      section.items.push(this.item)
    }
    this.updateInformable(informableId, info, informableType)
  }

  saveItem() {
    const { section, info, informableId, informableType, informableKey} = this.getSectionAndInfo(true)
    const itemIndex = this.getItemIndex(section, true)
    if (itemIndex > -1) {
      section.items[itemIndex] = this.item
    } else {
      section.items.push(this.item) // somebody deleted this while you were editing it?
    }
    this.updateInformable(informableId, info, informableType, informableKey)
  }

  updateInformable(informableId: string, info: InformableSection[], type: InformableType = null, informableKey: string = null) {
    if (this.data.notInformable) {
      this.dialogRef.close(true) // true means changes have taken place that need to be saved by the non informable informable
    } else {
      this.informableService.saveInformation(informableId, info, type, informableKey).then(_ => this.dialogRef.close())
    }
  }

  saveFiles(files) {
    this.item.files = files;
  }

  saveAssetIds(ids: string[]) {
    this.item.assetIds = [...(this.item.assetIds || []), ...ids]
  }

  removeAssetId(idToRemove: string) {
    this.item.assetIds = this.item.assetIds.filter(id => id !== idToRemove)
  }

}

export interface InformableEditInfo {
  notInformable?: boolean
  item?: InformableSectionItem
  sectionId?: string
  information: InformableSection[]
  informableId?: string
  informableType?: InformableType
  informableKey?: string
  itemPlaceholder?: string
  useAssetIds?: boolean
  assetEntityHolderName?: string
  descriptionPlaceholder?: string
  fromAsset?: boolean
}
